import useSWR from "swr";

import { usePassportProvider } from "@/context";
import { authorizedExternalGet, authorizedPost } from "@/utils/request";

import { appConfig } from "@/constants";
import { notifyError } from "@/utils/monitoring/newrelic";
import localforage from "localforage";
import { useGetWalletLatestEarnings } from "../api";
import { useAccessToken } from "../useAccessToken";
import { useOnboardingQuestData } from "./useOnboardingQuestData";
type TwitchDrop = {
  id: string;
  benefit_id: string;
  timestamp: string;
  user_id: string;
  game_id: string;
  fulfillment_status: "FULFILLED" | "CLAIMED";
  last_updated: string;
};

type TwitchDropResponse = {
  data: TwitchDrop[];
};

const TWITCH_DROPS_ACCESS_TOKEN_KEY = "twitch_access_token_";
const TWITCH_DROPS_ACCESS_TOKEN_EXPIRY_KEY = "twitch_access_token_expiry_";
const TWITCH_DROPS_REFRESH_TOKEN_KEY = "twitch_refresh_token_";
const TWITCH_DROPS_USERNAME_KEY = "twitch_username_";

export function getTwitchRedirectUrl(prismicGameUid: string) {
  return `${appConfig.BASE_REDIRECT_URL}/twitch-drops/${prismicGameUid}`;
}

export async function clearTwitchTokens() {
  const keys = await localforage.keys();
  for (const key of keys) {
    if (key.startsWith("twitch_")) {
      await localforage.removeItem(key);
    }
  }
}

export async function clearTwitchTokensForGame(imxGameId: string) {
  await localforage.removeItem(TWITCH_DROPS_ACCESS_TOKEN_KEY + imxGameId);
  await localforage.removeItem(TWITCH_DROPS_REFRESH_TOKEN_KEY + imxGameId);
  await localforage.removeItem(
    TWITCH_DROPS_ACCESS_TOKEN_EXPIRY_KEY + imxGameId,
  );
  await localforage.removeItem(TWITCH_DROPS_USERNAME_KEY + imxGameId);
}

async function storeTwitchTokens(
  accessToken: string,
  refreshToken: string,
  expiresIn: number,
  imxGameId: string,
) {
  await localforage.setItem(
    TWITCH_DROPS_ACCESS_TOKEN_KEY + imxGameId,
    accessToken,
  );
  await localforage.setItem(
    TWITCH_DROPS_REFRESH_TOKEN_KEY + imxGameId,
    refreshToken,
  );
  // 5 minutes before expiry
  await localforage.setItem(
    TWITCH_DROPS_ACCESS_TOKEN_EXPIRY_KEY + imxGameId,
    new Date().getTime() + expiresIn * 1000 - 5 * 60 * 1000,
  );
}

export async function twitchLogin(
  accessToken: string,
  code: string,
  imxGameId: string,
  prismicGameUid: string,
) {
  const response = await authorizedPost(
    "/v1/games/twitch/connect",
    {
      authorization_code: code,
      redirect_url: getTwitchRedirectUrl(prismicGameUid),
      game_id: imxGameId,
    },
    accessToken,
  );

  const { access_token, refresh_token, expires_in, username } = response;
  if (!access_token || !refresh_token || !expires_in || !username) {
    throw new Error("Invalid response format from Twitch authentication.");
  }

  // Specific for Treeverse
  if (imxGameId === "6253fded-0d07-4e41-8239-e935f6f5748e") {
    // Don't block the Twitch drops flow if the quest claim fails
    try {
      await authorizedPost(
        "/v1/rewards/claim",
        {
          event: "6253fded-0d07-4e41-8239-e935f6f5748e-twitch-drop-event",
        },
        accessToken,
      );
    } catch (error) {
      notifyError(error, "twitchDropQuestClaimError");
    }
  }

  await storeTwitchTokens(access_token, refresh_token, expires_in, imxGameId);
  await localforage.setItem(TWITCH_DROPS_USERNAME_KEY + imxGameId, username);

  return username;
}

export async function getTwitchUser(imxGameId: string) {
  const accessToken = await localforage.getItem(
    TWITCH_DROPS_ACCESS_TOKEN_KEY + imxGameId,
  );
  const refreshToken = await localforage.getItem(
    TWITCH_DROPS_REFRESH_TOKEN_KEY + imxGameId,
  );
  const accessTokenExpiry = await localforage.getItem(
    TWITCH_DROPS_ACCESS_TOKEN_EXPIRY_KEY + imxGameId,
  );
  const username = (await localforage.getItem(
    TWITCH_DROPS_USERNAME_KEY + imxGameId,
  )) as string | null;
  if (!accessToken || !refreshToken || !accessTokenExpiry || !username) {
    return null;
  }
  return { username };
}

async function getTwitchAccessToken(
  prismicGameUid: string,
  imxGameId: string,
  passportAccessToken: string,
) {
  const accessToken = (await localforage.getItem(
    TWITCH_DROPS_ACCESS_TOKEN_KEY + imxGameId,
  )) as string | null;
  const accessTokenExpiry = (await localforage.getItem(
    TWITCH_DROPS_ACCESS_TOKEN_EXPIRY_KEY + imxGameId,
  )) as number | null;
  const refreshToken = (await localforage.getItem(
    TWITCH_DROPS_REFRESH_TOKEN_KEY + imxGameId,
  )) as string | null;
  const username = (await localforage.getItem(
    TWITCH_DROPS_USERNAME_KEY + imxGameId,
  )) as string | null;
  if (!accessToken || !accessTokenExpiry || !refreshToken || !username) {
    await clearTwitchTokensForGame(imxGameId);
    throw new Error("Missing tokens");
  }
  if (new Date().getTime() > accessTokenExpiry) {
    const response = await authorizedPost(
      "/v1/games/twitch/connect",
      {
        refresh_token: refreshToken,
        redirect_url: getTwitchRedirectUrl(prismicGameUid),
        game_id: imxGameId,
      },
      passportAccessToken,
    );
    const { access_token, refresh_token, expires_in } = response;
    if (!access_token || !refresh_token || !expires_in) {
      await clearTwitchTokensForGame(imxGameId);
      throw new Error("Invalid response format from Twitch token refresh.");
    }

    await storeTwitchTokens(access_token, refresh_token, expires_in, imxGameId);
    return access_token;
  }
  return accessToken;
}

export function useGetTwitchDrops(
  imxGameId: string,
  clientId: string,
  isLoggedInToTwitch: boolean,
  prismicGameUid: string,
) {
  const getAccessToken = useAccessToken();
  const shouldFetch = imxGameId && isLoggedInToTwitch;
  const twitchGameId =
    imxGameId === "b6cc4d07-31af-4702-b011-ff231d738b4e"
      ? "509255"
      : imxGameId === "c07cb827-4590-4d14-b5d6-b47e1b34e74d" // elumia
        ? "891126860"
        : imxGameId === "1186b77c-7131-4cf4-8840-2e92d1df3bb3" // armourx
          ? "1510642132"
          : imxGameId === "da200b52-f706-433d-8617-03c35d02cdfe" // metatoy
            ? "126276139"
            : "1321308474";
  const requestUrl = `https://api.twitch.tv/helix/entitlements/drops?game_id=${twitchGameId}`;
  const { data, error, isLoading } = useSWR(
    shouldFetch ? requestUrl : null,
    async (path: string) => {
      const passportAccessToken = await getAccessToken();
      if (!passportAccessToken) {
        throw new Error("No passport access token");
      }
      const accessToken = await getTwitchAccessToken(
        prismicGameUid,
        imxGameId,
        passportAccessToken,
      );
      return authorizedExternalGet<TwitchDropResponse>(path, accessToken, {
        "Client-ID": clientId,
      });
    },
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    },
  );
  return { data, error, isLoading };
}

export function useGetRemainingEarnableGems(imxGameId: string) {
  const { walletAddress } = usePassportProvider();
  const questsData = useOnboardingQuestData(imxGameId);
  const totalGems = questsData.questsGroups.reduce((acc, group) => {
    return acc + group.totalGemsGranted;
  }, 0);
  const allQuests = questsData.questsGroups.flatMap((group) => group.quests);
  const { latestEarnings } = useGetWalletLatestEarnings(
    walletAddress ?? undefined,
  );
  const relevantEarnings = latestEarnings?.earnings.filter((earning) => {
    return allQuests.some((quest) => quest.ruleId === earning.ruleId);
  });
  const earnedGems = relevantEarnings?.reduce((acc, earning) => {
    return acc + earning.total;
  }, 0);
  return totalGems - (earnedGems ?? 0);
}
